import { type FC, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useGetEnrollmentCashEquivalentData, useSubmitEnrollmentCashEquivalentData } from '@egw/api'
import { Logger } from '@egw/logger'
import { EnrollmentLayout } from '@egw/layouts'
import { useRouter } from '@egw/router'
import { WEALTH_ROUTES } from '@egw/constants'
import { CashEquivalentCategory } from '@egw/api/src/models/enrollment/enrollment'
import { useTranslation } from '@egw/i18n'
import { ROUTES } from '../../../constants'
import { CashEquivalent } from '../../../components/cash-equivalent/cash-equivalent'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'

const log = new Logger('Enrollment - Cash Equivalent')

interface FormData {
  cashEquivalentCategory: string
}

export const CashEquivalentPage: FC = () => {
  const { navigate } = useRouter()
  const { t } = useTranslation('features-enrollment')

  const { data, isLoading, error } = useGetEnrollmentCashEquivalentData()
  const { mutate: submit, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentCashEquivalentData()

  const recommended = data?.enrollmentData.recommendation.investment.cashEquivalentType.recommended
  const allowTripleTaxFreeMunicipalMutualFund =
    data?.enrollmentData.recommendation.investment.cashEquivalentType.allowTripleTaxFreeMunicipalMutualFund
  const riskTolerance = data?.enrollmentData.userInfo.investment.riskTolerance

  const updatedCashEquivalentItems = useMemo(
    () =>
      Object.values(CashEquivalentCategory)
        .filter(
          (item) => !(item === CashEquivalentCategory.TripleTaxFree && allowTripleTaxFreeMunicipalMutualFund === false),
        )
        .map((item) => {
          const isRecommended = item === recommended
          return {
            value: item,
            label: t(`investments.cash-equivalent.options.${item}`),
            ...(isRecommended && { recommended: true }),
          }
        }),
    [allowTripleTaxFreeMunicipalMutualFund, recommended, t],
  )

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<FormData>({
    defaultValues: {
      cashEquivalentCategory: '',
    },
  })

  useValidateRoute(data?.path)

  const onSubmit = handleSubmit(async (fData: unknown): Promise<void> => {
    const formData = fData as FormData

    if (formData.cashEquivalentCategory) {
      try {
        const response = await submit({
          investmentProfile: {
            cashEquivalentCategory: formData.cashEquivalentCategory as CashEquivalentCategory,
          },
        })
        if (response?.path) {
          navigate(response.path)
        } else {
          // TODO: Handle the case when path is not returned
          navigate(ROUTES.HOME.path)
        }
      } catch (e) {
        log.error('Error submitting email', e)
      }
    }
  })

  return (
    <EnrollmentLayout className="bg-background-light" title="Cash Equivalent">
      <CashEquivalent
        items={updatedCashEquivalentItems}
        riskTolerance={riskTolerance}
        onSubmit={() => void onSubmit()}
        isLoading={isLoading}
        isSubmitting={isSubmitting}
        error={error}
        submitError={submitError}
        control={control}
        isValid={isValid}
        allowTripleTaxFreeMunicipalMutualFund={allowTripleTaxFreeMunicipalMutualFund}
      />
    </EnrollmentLayout>
  )
}
